import React, { useEffect, useRef, useState } from 'react';

import Tooltip from '@/components/common/Tooltip';
import { Color } from '@/components/common/libraries/types';
import { userInfo } from '@/components/common/libraries/actions';
import Status from '@/components/common/libraries/common/Status';
import { isEllipsisActive } from '@/components/common/libraries/common/utils';
import { VideoInfo } from '@/components/common/libraries/VideoLibrary/types';
import { hideIncludedIns } from '@/components/common/libraries/VideoLibrary/actions';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import { LinkButton } from '@/components/ui/Buttons';
import style from './VideoCard.module.scss';

interface Props {
  data: VideoInfo;
  onPlayVideo: (videoInfo: VideoInfo) => void;
  onPublish: (id: string | number | undefined, title: string) => void;
  onUnpublish: (id: string | number | undefined, title: string) => void;
  showPublish: boolean;
}

const generateKey = (id: string | number, string: string) => {
  const binary = new TextEncoder().encode(`${id}-${string}`);

  return btoa(String.fromCharCode(...binary));
};

const VideoCard = ({
  data,
  onPlayVideo,
  showPublish,
  onPublish,
  onUnpublish,
}: Props) => {
  const unitRef = useRef(null);
  const lessonRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState({
    unit: false,
    lesson: false,
  });

  const isStudent = !userInfo.staffer;

  useEffect(() => {
    setShowTooltip({
      unit: unitRef.current ? isEllipsisActive(unitRef.current) : false,
      lesson: lessonRef.current ? isEllipsisActive(lessonRef.current) : false,
    });
  }, [unitRef, lessonRef]);

  const handlePlayVideo = () => {
    onPlayVideo(data);
  };

  const handlePublishClick = () => {
    if (data.published) {
      onUnpublish(data.id, data.title);
    } else {
      onPublish(data.id, data.title);
    }
  };

  return (
    <div className={`card ${style.videoCard}`}>
      <div
        className={style.cardImg + (!data.poster_url ? ` ${style.blackImg}` : '')}
        onClick={handlePlayVideo}
      >
        <img src={data.poster_url} />
        <div className={style.playButton}>
          <Icon className="tw-text-tci-purple hover:tw-brightness-150" icon="Circle" size={40}>
            <Icon className="tw-text-white" icon="Play" size={5} x={2} y={2} />
          </Icon>
        </div>

        {data.video_duration && (
          <div className={`${style.videoLength} pos_bottom_right`}>
            {data.video_duration}
          </div>
        )}
      </div>

      <div className={style.cardContent}>
        <LinkButton className="tw-text-start tw-p-0" onClick={handlePlayVideo}>
          <Text className={style.cardTitle} weight="bold">
            {data.title}
          </Text>
        </LinkButton>

        <div className="mb10">
          {data.unit_title && (
            <Tooltip
              key={generateKey(data.id, data.unit_title)}
              content={data.unit_title}
              shouldDisplay={showTooltip.unit}
            >
              <div ref={unitRef} className={style.cardSubtitle}>
                <Text size="xs">{data.unit_title}</Text>
              </div>
            </Tooltip>
          )}
          {data.lesson_title && (
            <Tooltip
              key={generateKey(data.id, data.lesson_title)}
              content={data.lesson_title}
              shouldDisplay={showTooltip.lesson}
            >
              <div ref={lessonRef} className={style.cardSubtitle}>
                <Text size="xs">{data.lesson_title}</Text>
              </div>
            </Tooltip>
          )}
        </div>

        {!isStudent && data.included_in && (
          !hideIncludedIns.includes(data.included_in) && (
            <div className={cn('tw-flex', style.included_in)}>
              <Text size="xs">Go to&nbsp;</Text>
              {data.included_in_link ? (
                <a
                  className="bold tw-flex tw-items-end"
                  href={data.included_in_link}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Text size="xs">{data.included_in}</Text>
                  <Icon className="tw-ml-1" icon="ExternalLink" size={16} />
                </a>
              ) : (
                data.included_in
              )}
            </div>
          )
        )}

        {showPublish && (
          <button
            className={cn('btn btn--outline-purple tw-w-[7.5rem] tw-h-8',
              'tw-flex tw-items-center tw-gap-x-2 tw-mt-auto tw-self-start')}
            type="button"
            onClick={handlePublishClick}
          >
            <Status checked={data.published} />
            <Text size="sm">{data.published ? 'Published' : 'Publish'}</Text>
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoCard;
